import { Link, usePage } from "@inertiajs/react";

import backgroundGreen from "@/assets/background_green.svg";
import imageConvivencia from "@/assets/course_types/convivencia.webp";
import imageCursoAnual from "@/assets/course_types/curso_anual.webp";
import imageJornada from "@/assets/course_types/jornada.webp";
import imageRetiro from "@/assets/course_types/retiro.webp";
import imageLanding from "@/assets/image_landing.webp";
import Carousel from "@/components/Carousel";
import CourseTypeCard from "@/components/CourseTypeCard";
import HouseCard from "@/components/HouseCard";
import { courses, sessions } from "@/routes";
import type { PublicHouse, SharedProps } from "@/types";

type Props = {
  houses: PublicHouse[];
  show_new_web_banner: boolean;
};

/**
 * Section-level CTA per the mockup: teal pill, Inter SemiBold ~25 — noticeably
 * smaller than the shared form Button, so it gets its own classes.
 */
const SECTION_CTA_CLASSES =
  "rounded-full bg-steel px-6 py-2.5 text-center font-sans text-sm font-semibold uppercase tracking-wide text-white transition-colors hover:bg-steel/90";

const COURSE_TYPES = [
  {
    title: "Retiro",
    description:
      "Un espacio para detenerte, hacer una pausa y mirar tu vida con mayor profundidad. Días de silencio, reflexión y encuentro con Dios para volver a lo cotidiano con una nueva mirada.",
    image: imageRetiro,
    courseType: "retirement_course",
  },
  {
    title: "Convivencia",
    description:
      "Un encuentro para compartir, aprender y conversar sobre las preguntas que realmente importan: quiénes somos, qué le da sentido a nuestra vida y cómo podemos crecer junto a los demás.",
    image: imageConvivencia,
    courseType: "coexistence",
  },
  {
    title: "Curso anual",
    description:
      "Una oportunidad para salir del modo automático y dedicar tiempo a lo verdaderamente importante. Días de formación, estudio, conversación y vida en familia que te ayudarán a crecer y mirar el año con una nueva perspectiva.",
    image: imageCursoAnual,
    courseType: "yearly",
  },
  {
    title: "Jornada",
    description:
      "En medio de una vida acelerada, las jornadas ofrecen un espacio para detenerse, reflexionar y descubrir nuevas formas de ayudar a los demás y dejar una huella positiva en nuestro entorno.",
    image: imageJornada,
    courseType: "jornada",
  },
];

export default function Home({ houses, show_new_web_banner }: Props) {
  const { currentUser } = usePage<SharedProps>().props;
  const carouselItemHref = currentUser
    ? courses.index().url
    : sessions.new().url;
  const courseTypeHref = (courseType: string) => {
    const target = courses.index({ query: { course_type: courseType } }).url;
    return currentUser
      ? target
      : sessions.new({ query: { return_to: target } }).url;
  };
  const houseHref = (houseName: string) => {
    const target = courses.index({ query: { house: houseName } }).url;
    return currentUser
      ? target
      : sessions.new({ query: { return_to: target } }).url;
  };

  return (
    <>
      <section className="relative flex min-h-[420px] max-h-[60vh] items-end overflow-hidden sm:min-h-[560px]">
        <img
          src={imageLanding}
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-ink/20" />

        {/* Banner temporal — solo dev o host de Retiros y Cursos */}
        {show_new_web_banner && (
          <div className="absolute top-4 right-4 z-10 max-w-[80%] rounded-lg bg-steel/70 px-5 py-3 text-center font-sans text-lg font-semibold text-white shadow-sm sm:top-6 sm:right-6 sm:px-6 sm:py-4 sm:text-2xl">
            Nueva web: Retiros y Cursos
          </div>
        )}

        <div className="relative flex w-full flex-col gap-10 px-8 py-10 sm:px-12 sm:py-12">
          <div className="flex flex-col items-center gap-5 text-center text-white">
            <p className="font-sans text-lg font-semibold sm:text-xl">
              Cursos de formación cristiana
            </p>
            <Link
              href={carouselItemHref}
              className="max-w-full rounded-full border-2 border-white px-14 py-3 text-center font-sans text-xl font-semibold uppercase tracking-wide text-white transition-colors hover:bg-white hover:text-ink sm:w-1/2 sm:min-w-fit sm:py-4 sm:text-2xl"
            >
              Inscríbete aquí
            </Link>
          </div>

          <p className="font-display leading-[0.95] font-light text-white">
            <span className="block text-5xl sm:text-6xl">los</span>
            <span className="block text-7xl sm:text-8xl">tilos</span>
          </p>
        </div>
      </section>

      <section className="w-full pt-8 sm:pt-8">
        <div className="mx-auto w-full max-w-7xl px-6 sm:px-10">
          <h2 className="font-sans text-3xl leading-tight font-light text-ink sm:text-4xl">
            ¡Conoce nuestras
            <span className="block font-semibold">actividades!</span>
          </h2>
          <p className="mt-3 font-sans font-light text-ink">
            Revisa los tipos de cursos que ofrecemos
          </p>
        </div>

        <Carousel className="mt-8" autoRoll>
          {COURSE_TYPES.map((type) => (
            <CourseTypeCard
              key={type.title}
              title={type.title}
              description={type.description}
              image={type.image}
              href={courseTypeHref(type.courseType)}
            />
          ))}
        </Carousel>

        <div className="mx-auto mt-6 flex w-full max-w-7xl justify-end px-6 sm:px-10">
          <Link href={courses.index().url} className={SECTION_CTA_CLASSES}>
            Ver todos los cursos
          </Link>
        </div>
      </section>

      <section className="w-full py-8">
        <div className="relative flex min-h-[52vh] flex-col justify-center gap-x-10 overflow-hidden sm:min-h-[46vh] min-[900px]:grid min-[900px]:h-[34vh] min-[900px]:grid-cols-2 min-[900px]:overflow-visible">
          <div className="absolute inset-0 flex items-center justify-center overflow-hidden min-[900px]:relative min-[900px]:inset-auto min-[900px]:col-start-1 min-[900px]:row-start-1 min-[900px]:h-full min-[900px]:rounded-r-3xl">
            <img
              src={backgroundGreen}
              alt=""
              className="h-full w-full object-cover min-[900px]:h-[200%] min-[900px]:w-[200%] min-[900px]:rotate-90"
            />
          </div>

          <div className="relative z-10 px-6 pt-6 pb-2 text-left min-[900px]:col-start-1 min-[900px]:row-start-1 min-[900px]:flex min-[900px]:h-full min-[900px]:items-center min-[900px]:justify-end min-[900px]:p-6 min-[900px]:text-right">
            <div>
              <p className="font-display text-4xl font-light text-deep-red min-[900px]:text-5xl">
                Nuestra
              </p>
              <p className="font-display text-6xl font-semibold text-deep-red min-[900px]:text-7xl">
                misión
              </p>
            </div>
          </div>

          <div className="relative z-10 flex items-center px-6 py-2 pb-6 min-[900px]:col-start-2 min-[900px]:row-start-1 min-[900px]:h-full min-[900px]:pl-0 min-[900px]:pr-10">
            <p className="text-justify font-sans text-base font-light text-deep-red">
              Fundación Los Tilos gestiona y apoya el desarrollo de actividades de
              formación integral, de personas que quieran cultivar valores humanos
              y cristianos, impulsando su crecimiento personal para contribuir al 
              bien de la sociedad. Algunas de estas actividades son cursos de retiro, 
              convivencias de formación, jornadas y otras actividades recreativas y 
              culturales. La Fundación confía a la Prelatura del Opus Dei la 
              orientación cristiana y la atención espiritual de esas actividades.
            </p>
          </div>
        </div>
      </section>

      <section className="w-full pb-8">
        <div className="mx-auto w-full max-w-7xl px-6 sm:px-10">
          <h2 className="font-sans text-3xl leading-tight font-light text-ink sm:text-4xl">
            Diferentes opciones
            <span className="block font-semibold">para ti</span>
          </h2>
          <p className="mt-3 font-sans font-light text-ink">
            Conoce las casas que tenemos a disposición
          </p>
        </div>

        <Carousel className="mt-8" autoRoll>
          {houses.map((house) => (
            <HouseCard
              key={house.id}
              name={house.name ?? ""}
              communeName={house.commune_name}
              description={house.description}
              image={house.image_url}
              fallbackImage={imageLanding}
              href={houseHref(house.name ?? "")}
            />
          ))}
        </Carousel>

        <div className="mx-auto mt-6 flex w-full max-w-7xl justify-end px-6 sm:px-10">
          <Link href={courses.index().url} className={SECTION_CTA_CLASSES}>
            Ver todos los cursos
          </Link>
        </div>
      </section>
    </>
  );
}
